import { EventEmitter, Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { User } from '../../shared/data-objects/user';
import { ServiceBase } from './service-base';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Buffer } from 'buffer';
import * as moment from 'moment'
import { ReadKeyExpr } from '@angular/compiler';
import { environment } from '../../../app-environments/environment';

@Injectable()
export class LoginService extends ServiceBase {

  STORAGE_SESSION: { user: string, tokenExpiry: string, continueSession: string } = { user: '__user', tokenExpiry: '__tokenExpireAt', continueSession: '__continueSession' };

  onLogout: EventEmitter<Boolean>;

  public static get logoutUrl(): string {
    return ServiceBase.baseHref + '/api/logoutserver';
  }

  public static get loginUrl(): string {
    return ServiceBase.baseHref + '/authenticate';
  }

  public static get guestLoginUrl(): string {
    return ServiceBase.baseHref + '/guestlogin';
  }

  public static get forgotPasswordUrl(): string {
    return ServiceBase.baseHref + '/forgotpassword';
  }

  public static get resetPasswordUrl(): string {
    return ServiceBase.baseHref + '/resetpassword';
  }

  public static get fetchUserDetailsFromTokenUrl(): string {
    return ServiceBase.baseHref + '/user_details';
  }

  public static get registerEmailUrl(): string {
    return ServiceBase.baseHref + '/register_email';
  }

  public static get registerUrl(): string {
    return ServiceBase.baseHref + '/register';
  }

  public static get updateUrl(): string {
    return ServiceBase.baseHref + '/api/users/${userid}/details';
  }

  public static get updatePasswordUrl(): string {
    return ServiceBase.baseHref + '/api/users/${userid}/password';
  }

  public static get getMyUsersUrl(): string {
    return ServiceBase.baseHref + '/api/users/';
  }

  public static get getServerVariablesUrl(): string {
    return ServiceBase.baseHref + '/vars';
  }

  public static get getSettingsUrl(): string {
    return ServiceBase.baseHref + '/assets/settings.json';
  }

  public static get getVerifyAccountUrl(): string {
    return ServiceBase.baseHref + '/api/getaccountdetails';
  }


  private user: User;

  constructor(private http: HttpClient) {
    super();
    this.onLogout = new EventEmitter<Boolean>();
  }

  getToken(): string {
    // return this.user.token;
    let token = '';
    if (this.user) {
      token = this.user.token;
    }

    if (!token) {
      token = this.getLocalUser('token');
    }

    return token;
  }

  getUser(): User {
    let user = this.user;
    if (!user) {
      const _cookieUsr = this.getLocalUser();
      if (_cookieUsr) {
        user = _cookieUsr;
      }
    }
    return user;
  }

  getAccountNumber(): string {
    let an = '';
    if (this.user) {
      an = this.user.accountNumber;
    }

    if (!an) {
      an = this.getLocalUser('accountNumber');
    }

    return an;
  }

  private getCookie(_cookie: string) {
    return localStorage.getItem(_cookie);
  }
  private setCookie(_key: string, _val: string) {
    return localStorage.setItem(_key, _val);
  }
  private deleteCookie(_key: string) {
    return localStorage.removeItem(_key);
  }
  // gets user object from local storage of browser
  private getLocalUser(param: string = '') {
    let retVal: any = null;
    // decode
    let user = '';
    const user_encoded = this.getCookie(this.STORAGE_SESSION.user);
    if (user_encoded) {
      try {
        user = JSON.parse(Buffer.from(user_encoded, 'base64').toString('ascii'));
      } catch (e) {
        this.clearUserSession();
      }
    }

    if (user) {
      switch (param) {
        case 'token':
          retVal = user['token'];
          break;
        case 'user_role':
          retVal = user['userRole'];
          break;
        case 'accountNumber':
          retVal = user['accountNumber'];
          break;
        default:
          retVal = user;
          break;
      }
    }

    return retVal;
  }

  async verifyToken(token: string): Promise<any> {
    if (!token) return { valid: false };

    const header: HttpHeaders = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('Authorization', 'Bearer ' + token);

    try {
      let result = await this.http.get<any>(ServiceBase.baseHref + '/verifytoken', { headers: header }).toPromise();
      return { valid: true };
    } catch (err) {
      return { valid: false };
    }
  }

  async initUser(): Promise<void> {
    if (!this.user) this.user = this.getUser();
  }

  // use current valid token to get a new token.
  // this method is usually called when starting a new application (in onInit method)
  // doing refreshing can ensure that customer won't encounter expired token during the application
  async refreshToken(): Promise<void> {

  }

  // This func is called from a modal popup at user session exiry.
  // extend user session token by 30 min.
  async extendToken() {
    let currentToken = this.getLocalUser('token');
    if (!currentToken) return;

    const header: HttpHeaders = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('Authorization', 'Bearer ' + currentToken);

    try {
      if (this.user && this.user.username) {
        let current_expiry: any = this.getCookie(this.STORAGE_SESSION.tokenExpiry);
        if (environment.envName !== 'local') {
          current_expiry = moment(current_expiry, 'DD-MM-YYYY, HH:mm:ss').utc().format('DD-MM-YYYY, HH:mm:ss');
        }

        let newTokenResponse = await this.http.post<any>(ServiceBase.baseHref + '/extendToken',
          { username: this.user.username, tokenExpiry: current_expiry }, { headers: header }).toPromise();
        if (newTokenResponse.message === 'success' && newTokenResponse.newToken) {
          const newToken = newTokenResponse.newToken;
          const expiryDT = (JSON.parse(atob(newToken.split('.')[1]))).exp;
          const user_b64encoded = Buffer.from(JSON.stringify(this.user)).toString('base64');

          const expiry = moment(expiryDT * 1000).local().format('DD-MM-YYYY, HH:mm:ss');

          this.setCookie(this.STORAGE_SESSION.tokenExpiry, expiry);
          this.setCookie(this.STORAGE_SESSION.user, user_b64encoded);
        }
      }
    } catch (err) { }
  }

  async isTokenValid(): Promise<boolean> {
    let userToken = this.getLocalUser('token');
    let verification = await this.verifyToken(userToken);
    return verification ? verification.valid : false;
  }

  clearUserSession() {
    this.setCookie(this.STORAGE_SESSION.tokenExpiry, '');
    this.setCookie(this.STORAGE_SESSION.user, '');
    this.setCookie(this.STORAGE_SESSION.continueSession, '');

    this.deleteCookie('tokenExpireAt');
    this.deleteCookie(this.STORAGE_SESSION.user);
    this.deleteCookie(this.STORAGE_SESSION.tokenExpiry);
    this.deleteCookie(this.STORAGE_SESSION.continueSession);
  }

  // get session expiry from local storage of browser
  getLocalUserSessionExpiry() {
    const expiry = this.getCookie(this.STORAGE_SESSION.tokenExpiry);
    return ['', null, undefined].indexOf(expiry) > 0 ? null : expiry;
  }

  getLocalUserSessionContinueState() {
    const continueSession = this.getCookie(this.STORAGE_SESSION.continueSession);
    return ['', null, undefined].indexOf(continueSession) > 0 ? null : continueSession;
  }

  async setLocalUserSessionContinueState(state?: boolean) {
    switch (state) {
      case true:
        await this.extendToken();

        let expiry: any = this.getLocalUserSessionExpiry();
        expiry = moment(expiry, 'DD-MM-YYYY, HH:mm:ss');
        expiry = expiry.add(30, 'minutes');
        // this.setCookie(this.STORAGE_SESSION.continueSession, 'yes');
        this.deleteCookie(this.STORAGE_SESSION.continueSession);
        break;
      case false:
        this.setCookie(this.STORAGE_SESSION.continueSession, 'no');
        break;
      default:
        this.setCookie(this.STORAGE_SESSION.continueSession, '');
        break;
    }
  }

  isUserLoggedIn(): boolean {
    let userToken = this.getLocalUser('token');
    if (!userToken) return false;
    return !!userToken || (this.user && this.user.token && this.user.token !== '');
  }

  loggedInUserDetails() {
    let userToken = this.getLocalUser('token');

  }

  isGuestUserLoggedIn(): boolean {
    let userRole = this.getLocalUser('user_role');
    return (userRole && (userRole || '').toLowerCase() === 'guest')
      || (this.user && this.user.token && this.user.token !== '' && this.user.userRole === 'guest');
  }

  isUser(): boolean {
    let userRole = this.getLocalUser('user_role');
    if (!userRole) return false;
    return (userRole && (userRole || '').toLowerCase() === 'user')
      || (this.user && this.user.token && this.user.token !== '' && this.user.userRole && this.user.userRole.toLowerCase() === 'user');
  }

  isAdminUserLoggedIn(): boolean {
    let userRole = this.getLocalUser('user_role');
    if (!userRole) return false;
    return (userRole && (userRole || '').toLowerCase() === 'admin')
      || (this.user && this.user.token && this.user.token !== '' && this.user.userRole && this.user.userRole.toLowerCase() === 'admin');
  }

  isSystemAdminUserLoggedIn(): boolean {
    let userRole = this.getLocalUser('user_role');
    if (!userRole) return false;
    return (userRole && (userRole || '').toLowerCase() === 'sysadmin')
      || (this.user && this.user.token && this.user.token !== '' && this.user.userRole && this.user.userRole.toLowerCase() === 'sysadmin');
  }

  isTechCSOUserLoggedIn(): boolean {
    let userRole = this.getLocalUser('user_role');
    if (!userRole) return false;
    return (userRole && (userRole || '').toLowerCase() === 'techcso')
      || (this.user && this.user.token && this.user.token !== '' && this.user.userRole && this.user.userRole.toLowerCase() === 'techcso');
  }

  isUserBillLater(): boolean {
    return this.getUser() ? !!(this.getUser().billLater) : false;
  }

  loadSettings(): Observable<any | {}> {
    const header: HttpHeaders = new HttpHeaders().append('Content-Type', 'application/json');
    return this.http.get(LoginService.getSettingsUrl, { headers: header });
  }

  logout(okCb: (boolean) => void, errorCb?: (any) => void): void {
    if (this.user || this.isUserLoggedIn()) {
      const userToken = this.user && this.user.token ? this.user.token : this.getLocalUser('token');

      const header: HttpHeaders = new HttpHeaders()
        .append('Content-Type', 'application/json')
        .append('Authorization', 'Bearer ' + userToken);
      this.http.post<boolean>(LoginService.logoutUrl, null, { headers: header })
        .subscribe(data => {
          // this.user.token = '';
          this.user = null;
          this.onLogout.emit(true);
          okCb(true);

          this.clearUserSession();
        }, err => {
          // this.user.token = '';
          this.user = null;
          this.onLogout.emit(true);
          ServiceBase.handleError(err, errorCb);

          this.clearUserSession();
        });
    }
  }

  login(username: string, password: string, okCb: (boolean) => void, errorCb?: (any) => void): void {
    this.http.post<User>(LoginService.loginUrl, {
      username: username,
      password: (password || '').trim()
    },
      { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) })
      .subscribe(data => {
        this.onLogout.emit(true);
        this.user = data;
        const retVal = this.hasOwnProperty('user') && this.user && this.user.hasOwnProperty('token') &&
          this.user.token && this.user.token !== '';

        if (this.user && this.user.userRole !== 'guest') {
          const userToken = this.user && this.user.token ? this.user.token : this.getLocalUser('token');
          const expiryDT = (JSON.parse(atob(userToken.split('.')[1]))).exp;
          const user_b64encoded = Buffer.from(JSON.stringify(this.user)).toString('base64');
          this.setCookie(this.STORAGE_SESSION.tokenExpiry, moment(expiryDT * 1000).format('DD-MM-YYYY, HH:mm:ss'));
          this.setCookie(this.STORAGE_SESSION.user, user_b64encoded);
        }

        okCb(retVal);
      }, err => {
        ServiceBase.handleError(err, errorCb);
      });
  }

  guestLogin(okCb: (boolean) => void, errorCb?: (any) => void): void {
    //if (this.isGuestUserLoggedIn()) {
    //  okCb(true);
    //} else {
    this.http.post<User>(LoginService.guestLoginUrl, {
      username: '',
      password: ''
    },
      { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) })
      .subscribe(data => {
        this.user = data;
        const userToken = this.user && this.user.token ? this.user.token : this.getLocalUser('token');
        const expiryDT = (JSON.parse(atob(userToken.split('.')[1]))).exp;
        const user_b64encoded = Buffer.from(JSON.stringify(this.user)).toString('base64');
        this.setCookie(this.STORAGE_SESSION.tokenExpiry, moment(expiryDT * 1000).format('DD-MM-YYYY, HH:mm:ss'));
        this.setCookie(this.STORAGE_SESSION.user, user_b64encoded);

        okCb(userToken !== '');
      }, err => {
        ServiceBase.handleError(err, errorCb);
      });
    //}
  }

  getServerVariablesUrl(): Observable<any | {}> {
    const header: HttpHeaders = new HttpHeaders().append('Content-Type', 'application/json');
    return this.http.get(LoginService.getServerVariablesUrl, { headers: header });
  }

  forgotPassword(username: string): Observable<boolean | {}> {
    return this.http.post<boolean>(LoginService.forgotPasswordUrl, {
      username: username
    },
      { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) });
  }

  updatePassword(token: string, password: string): Observable<boolean | {}> {
    return this.http.post<boolean>(LoginService.resetPasswordUrl, {
      token: token,
      password: (password || '').trim()
    },
      { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) });
  }

  getUserDetailsFromToken(email: string, token: string) {
    return this.http.post(LoginService.fetchUserDetailsFromTokenUrl, { email, token }, { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) });
  }

  registerEmail(email: string) {
    return this.http.post(LoginService.registerEmailUrl, { email }, { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) });
  }

  register(username: string, password: string, user: User, okCb: (boolean) => void, errorCb?: (any) => void): void {
    const applicant: User = ServiceBase.sanitize(user);
    this.http.post(
      LoginService.registerUrl,
      {
        username: username,
        password: password,
        title: applicant.title,
        firstName: applicant.firstName,
        lastName: applicant.lastName,
        accountNumber: applicant.accountNumber,
        email: applicant.email,
        landLine: applicant.landLine,
        mobile: applicant.mobile,
        abnNumber: applicant.abnNumber || '',
        companyName: applicant.companyName || '',
        structuredPostal: applicant.structuredPostal || null
      },
      { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) }
    ).subscribe(_ => {
      this.user = null;
      okCb(true);
    }, err => {
      ServiceBase.handleError(err, errorCb);
    });
  }

  updateUser(user: User, updateCurrentUser: boolean, okCb: (boolean, User) => void,
    errorCb?: (HttpErrorResponse, User) => void): void {
    const applicant: User = ServiceBase.sanitize(user);
    const userToken = this.user && this.user.token ? this.user.token : this.getLocalUser('token');

    const header: HttpHeaders = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('Authorization', 'Bearer ' + userToken);
    const url = this.updateUserUrl(updateCurrentUser ? this.user : applicant);
    this.http.put(url, {
      username: applicant.username,
      title: applicant.title,
      firstName: applicant.firstName,
      lastName: applicant.lastName,
      companyName: applicant.companyName,
      abnNumber: applicant.abnNumber,
      accountNumber: applicant.accountNumber,
      email: applicant.email,
      landLine: applicant.landLine,
      mobile: applicant.mobile,
      approved: applicant.approved,
      userRole: applicant.userRole
    },
      { headers: header })
      .subscribe(data => {
        if (updateCurrentUser) {
          this.user.title = applicant.title;
          this.user.firstName = applicant.firstName;
          this.user.lastName = applicant.lastName;
          this.user.accountNumber = applicant.accountNumber;
          this.user.abnNumber = applicant.abnNumber;
          this.user.companyName = applicant.companyName;
          this.user.email = applicant.email;
          this.user.landLine = applicant.landLine;
          this.user.mobile = applicant.mobile;
          this.user.isNewAccount = false;

          applicant.isNewAccount = false;
        }
        okCb(true, applicant);
      }, err => {
        if (err.error instanceof Error) {
          // A client-side or network error occurred. Handle it accordingly.
          console.error('An error occurred:', err.error.message);
        } else {
          // The backend returned an unsuccessful response code.
          // The response body may contain clues as to what went wrong,
          console.error(`Backend returned code ${err.status}, body was: ${err.error}`);
          if (errorCb) {
            errorCb(err, applicant);
          }
        }
      });
  }

  changePassword(currentPassword: string, newPassword: string, okCb: (boolean) => void, errorCb?: (any) => void): void {
    const userToken = this.user && this.user.token ? this.user.token : this.getLocalUser('token');

    const header: HttpHeaders = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('Authorization', 'Bearer ' + userToken);
    const url = this.updateCurrentUserPasswordUrl();
    this.http.put(url, {
      currentPassword: (currentPassword || '').trim(),
      password: (newPassword || '').trim()
    },
      { headers: header })
      .subscribe(data => {
        okCb(true);
      }, err => {
        ServiceBase.handleError(err, errorCb);
      });
  }

  updateCurrentUserPasswordUrl() {
    return this.updateUrlForUser(LoginService.updatePasswordUrl, this.user);
  }

  updateUserUrl(u: User) {
    return this.updateUrlForUser(LoginService.updateUrl, u);
  }

  private updateUrlForUser(url: string, u: User): string {
    const userId = encodeURI(u.username);
    return url.replace('${userid}', userId);
  }

  getMyUsers(): Observable<User[]> {
    const userToken = this.user && this.user.token ? this.user.token : this.getLocalUser('token');

    const header: HttpHeaders = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('Authorization', 'Bearer ' + userToken);

    const url: string = LoginService.getMyUsersUrl;
    return this.http.get<User[]>(url, { headers: header });
  }

  verifyAccount(accountNumber: string): Observable<User> {
    const userToken = this.user && this.user.token ? this.user.token : this.getLocalUser('token');

    const params: HttpParams = new HttpParams().set('accountNumber', accountNumber);
    const header: HttpHeaders = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('Authorization', 'Bearer ' + userToken);

    const url: string = LoginService.getVerifyAccountUrl;
    return this.http.get<User>(url, { headers: header, params: params });
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/services/login.service.ts